import React from 'react';

const MissionModal = ({ isOpen, onClose, language }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {language === 'english' ? 'Our Mission' : 'हमारा मिशन'}
                </h2>
                <p className="text-orange-100">
                  {language === 'english' ? 'Detailed Mission Information' : 'विस्तृत मिशन जानकारी'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {language === 'english' ? (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission in Action</h3>
                <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full"></div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                    Cricket Promotion & Development
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To promote cricket and provide a comprehensive platform for talented players to showcase their skills while fostering sportsmanship, community spirit, and the overall development of youth through innovative sports programs and physical activities.
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span>Organize regular cricket tournaments and competitions</li>
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span>Provide professional coaching and training facilities</li>
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span>Develop grassroots cricket programs for all age groups</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                    Youth Development Programs
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To create comprehensive youth development programs that focus on physical fitness, mental well-being, character building, and life skills development through sports.
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span>Physical fitness and health monitoring programs</li>
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span>Mental health awareness and stress management</li>
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span>Leadership and teamwork development workshops</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                    Community Engagement
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To strengthen community bonds through sports activities, promoting unity, healthy competition, and social cohesion among different age groups and backgrounds.
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Community sports festivals and celebrations</li>
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Inter-community tournaments and friendly matches</li>
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>Volunteer programs and community service initiatives</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                    Talent Identification & Nurturing
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To identify, nurture, and develop emerging sporting talent, providing them with the necessary resources, guidance, and opportunities to excel in their chosen sports.
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span>Comprehensive talent scouting programs</li>
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span>Advanced coaching and skill development</li>
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span>Pathways to higher-level competitions and recognition</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                    Infrastructure Development
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    To advocate for and support the development of modern sports infrastructure, ensuring access to quality facilities and equipment for all community members.
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-red-500 mr-2">•</span>Modern training facilities and equipment upgrades</li>
                    <li className="flex items-start"><span className="text-red-500 mr-2">•</span>Collaboration with government and private sectors</li>
                    <li className="flex items-start"><span className="text-red-500 mr-2">•</span>Sustainable development practices in sports infrastructure</li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl text-white text-center">
                <h4 className="text-xl font-bold mb-2">Our Commitment</h4>
                <p className="text-orange-100">
                  We are dedicated to fulfilling our mission through innovative programs, dedicated efforts, and unwavering commitment to the development of our community's youth and sports culture.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">कार्य में हमारा मिशन</h3>
                <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full"></div>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                    क्रिकेट प्रचार एवं विकास
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    क्रिकेट को बढ़ावा देना और प्रतिभाशाली खिलाड़ियों को अपने कौशल प्रदर्शित करने का व्यापक मंच प्रदान करना, साथ ही नवाचारी खेल कार्यक्रमों और शारीरिक गतिविधियों के माध्यम से खेल भावना, सामुदायिक भावना और युवाओं के समग्र विकास को बढ़ावा देना।
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span>नियमित क्रिकेट टूर्नामेंट और प्रतियोगिताओं का आयोजन</li>
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span>पेशेवर कोचिंग और प्रशिक्षण सुविधाएं प्रदान करना</li>
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span>सभी आयु वर्गों के लिए जमीनी स्तर के क्रिकेट कार्यक्रम विकसित करना</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                    युवा विकास कार्यक्रम
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    व्यापक युवा विकास कार्यक्रम बनाना जो खेलों के माध्यम से शारीरिक फिटनेस, मानसिक कल्याण, चरित्र निर्माण और जीवन कौशल विकास पर केंद्रित हों।
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span>शारीरिक फिटनेस और स्वास्थ्य निगरानी कार्यक्रम</li>
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span>मानसिक स्वास्थ्य जागरूकता और तनाव प्रबंधन</li>
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span>नेतृत्व और टीमवर्क विकास कार्यशालाएं</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                    सामुदायिक सहभागिता
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    खेल गतिविधियों के माध्यम से सामुदायिक बंधनों को मजबूत करना, विभिन्न आयु वर्गों और पृष्ठभूमि के लोगों के बीच एकता, स्वस्थ प्रतिस्पर्धा और सामाजिक सामंजस्य को बढ़ावा देना।
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>सामुदायिक खेल त्योहार और समारोह</li>
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>अंतर-सामुदायिक टूर्नामेंट और मैत्रीपूर्ण मैच</li>
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span>स्वयंसेवक कार्यक्रम और सामुदायिक सेवा पहल</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
                    प्रतिभा पहचान एवं पोषण
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    उभरती खेल प्रतिभाओं की पहचान, पोषण और विकास करना, उन्हें अपने चुने हुए खेलों में उत्कृष्टता प्राप्त करने के लिए आवश्यक संसाधन, मार्गदर्शन और अवसर प्रदान करना।
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span>व्यापक प्रतिभा खोज कार्यक्रम</li>
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span>उन्नत कोचिंग और कौशल विकास</li>
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span>उच्च स्तरीय प्रतियोगिताओं और पहचान के लिए मार्ग</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-6">
                  <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
                    अवसंरचना विकास
                  </h4>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    आधुनिक खेल अवसंरचना के विकास के लिए वकालत करना और समर्थन करना, सभी सामुदायिक सदस्यों के लिए गुणवत्तापूर्ण सुविधाओं और उपकरणों तक पहुंच सुनिश्चित करना।
                  </p>
                  <ul className="text-gray-600 space-y-2 ml-6">
                    <li className="flex items-start"><span className="text-red-500 mr-2">•</span>आधुनिक प्रशिक्षण सुविधाएं और उपकरण उन्नयन</li>
                    <li className="flex items-start"><span className="text-red-500 mr-2">•</span>सरकारी और निजी क्षेत्रों के साथ सहयोग</li>
                    <li className="flex items-start"><span className="text-red-500 mr-2">•</span>खेल अवसंरचना में टिकाऊ विकास प्रथाएं</li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl text-white text-center">
                <h4 className="text-xl font-bold mb-2">हमारी प्रतिबद्धता</h4>
                <p className="text-orange-100">
                  हम नवाचारी कार्यक्रमों, समर्पित प्रयासों और हमारे समुदाय के युवाओं और खेल संस्कृति के विकास के प्रति अटूट प्रतिबद्धता के माध्यम से अपने मिशन को पूरा करने के लिए समर्पित हैं।
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MissionModal;
